"use client";

import { useCallback, useEffect, useRef } from "react";
import { useAnimate, type AnimationPlaybackControls } from "motion/react";
import { usePrefersReducedMotion } from "./use-prefers-reduced-motion";

interface MarqueeLoopOptions {
  /** Seconds for one full pass of the *doubled* track (0% → -50%). */
  duration: number;
}

/**
 * Seamless infinite marquee, driven by Motion.
 *
 * Attach the returned `scope` ref to a `w-max flex` track that renders
 * its content **twice**. The track is translated `0% → -50%` on a
 * linear infinite repeat: at -50% the second copy sits exactly where
 * the first started, so the loop boundary paints the same pixels and is
 * invisible. Motion drives the value every RAF tick, so there's none of
 * the single-frame discontinuity a CSS keyframe loop can leave at the
 * iteration boundary.
 *
 * `pause` / `resume` are exposed for hover/focus pausing (footer
 * brewery row). Honours `prefers-reduced-motion`: in that mode no
 * animation is started and the track sits at its initial position,
 * showing the content repeated statically.
 */
export function useMarqueeLoop({ duration }: MarqueeLoopOptions) {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const controlsRef = useRef<AnimationPlaybackControls | null>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (!scope.current) return;

    if (reduced) {
      // Snap back to the start position — covers a runtime toggle from the
      // animated state into reduced motion. Using `animate` (not a direct
      // style write) keeps the React Compiler happy, since `scope` isn't a
      // plain ref it lets us mutate.
      animate(scope.current, { x: "0%" }, { duration: 0 });
      return;
    }

    const controls = animate(
      scope.current,
      { x: ["0%", "-50%"] },
      { duration, ease: "linear", repeat: Infinity },
    );
    controlsRef.current = controls;

    return () => {
      controls.stop();
      controlsRef.current = null;
    };
  }, [animate, scope, duration, reduced]);

  const pause = useCallback(() => controlsRef.current?.pause(), []);
  const resume = useCallback(() => controlsRef.current?.play(), []);

  return { scope, pause, resume };
}
